import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Play,
  Loader2,
  CheckCircle2,
  XCircle,
  Clock,
  FlaskConical,
  ChevronLeft,
  ChevronRight,
  type LucideIcon,
} from 'lucide-react';
import {
  evaluationApi,
  type EvalRunStatus,
} from '../services/evaluationApi';

// Poll interval for in-progress eval runs — fixed at 7 seconds.
const POLL_MS = 7000;

const ALL_METRICS = ['kpi_exact_match'];
const PAGE_SIZE = 3;
const IN_PROGRESS = new Set(['PENDING', 'RUNNING']);

const statusStyle = (status: string): { cls: string; Icon: LucideIcon } => {
  switch (status.toUpperCase()) {
    case 'COMPLETED':
      return { cls: 'text-emerald-700 bg-emerald-50 border-emerald-200', Icon: CheckCircle2 };
    case 'FAILED':
      return { cls: 'text-red-700 bg-red-50 border-red-200', Icon: XCircle };
    case 'RUNNING':
      return { cls: 'text-blue-700 bg-blue-50 border-blue-200', Icon: Loader2 };
    default:
      return { cls: 'text-slate-700 bg-slate-50 border-slate-200', Icon: Clock };
  }
};

const RunCard: React.FC<{ run: EvalRunStatus }> = ({ run }) => {
  const st = (run.status || '').toUpperCase();
  const { cls, Icon: StatusIcon } = statusStyle(st);

  // Per-metric pass/fail. Prefer the exact backend breakdown; for older runs
  // that predate it, derive passed = round(avg × items) (exact for 0/1 metrics).
  const summaries = run.result?.metric_summaries;
  const averages = run.result?.average_scores || {};
  const totalItems = run.result?.total_items ?? 0;
  const metricRows = summaries
    ? Object.entries(summaries).map(([name, s]) => ({
        name,
        passed: s.passed,
        total: s.total,
        avg: s.average,
      }))
    : Object.entries(averages).map(([name, val]) => ({
        name,
        passed: Math.round((typeof val === 'number' ? val : 0) * totalItems),
        total: totalItems,
        avg: typeof val === 'number' ? val : 0,
      }));
  return (
    <div className="border border-light rounded-lg p-4 space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="font-semibold text-slate-800">{run.run_name || run.run_id}</div>
          <div className="text-xs text-slate-400 font-mono">{run.run_id}</div>
          {run.created_at && (
            <div className="text-xs text-slate-400">
              {new Date(run.created_at).toLocaleString()}
            </div>
          )}
        </div>
        <span
          className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border text-sm font-semibold ${cls}`}
        >
          <StatusIcon className={`w-4 h-4 ${st === 'RUNNING' ? 'animate-spin' : ''}`} />
          {st || 'PENDING'}
        </span>
      </div>

      {run.result && (
        <div className="space-y-2">
          <div className="text-sm text-slate-600">
            items evaluated <span className="font-semibold text-slate-800">{totalItems}</span>
            {(run.result.failed ?? 0) > 0 && (
              <>
                {' · '}errors{' '}
                <span className="font-semibold text-red-700">{run.result.failed}</span>
              </>
            )}
          </div>
          <div className="flex flex-wrap gap-x-5 gap-y-1">
            {metricRows.map((m) => {
              const failedCount = Math.max(0, m.total - m.passed);
              return (
                <div key={m.name} className="text-sm text-slate-600">
                  {m.name}:{' '}
                  <span className="font-semibold text-emerald-700">{m.passed} passed</span>
                  {' · '}
                  <span className="font-semibold text-red-700">{failedCount} failed</span>
                  <span className="text-slate-400"> · avg {m.avg.toFixed(4)}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {run.error && (
        <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
          {run.error}
        </div>
      )}
    </div>
  );
};

export const EvaluationsSection: React.FC = () => {
  const [datasetName, setDatasetName] = useState('test_eval');
  const [metrics, setMetrics] = useState<string[]>(['kpi_exact_match']);
  const [maxItems, setMaxItems] = useState('');
  const [mode, setMode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [runs, setRuns] = useState<EvalRunStatus[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loadingRuns, setLoadingRuns] = useState(false);

  const pageRef = useRef(0);
  const pollRef = useRef<number | null>(null);

  const clearPoll = useCallback(() => {
    if (pollRef.current !== null) {
      window.clearTimeout(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const fetchRuns = useCallback(
    async (p: number) => {
      pageRef.current = p;
      setLoadingRuns(true);
      try {
        const res = await evaluationApi.listRuns(PAGE_SIZE, p * PAGE_SIZE);
        setRuns(res.runs);
        setTotal(res.total);
        setPage(p);
        // Keep polling (re-fetch current page) while any visible run is in progress.
        clearPoll();
        if (res.runs.some((r) => IN_PROGRESS.has((r.status || '').toUpperCase()))) {
          pollRef.current = window.setTimeout(() => fetchRuns(pageRef.current), POLL_MS);
        }
      } catch (e: any) {
        setError(e?.response?.data?.detail || e?.message || 'Failed to load runs');
      } finally {
        setLoadingRuns(false);
      }
    },
    [clearPoll],
  );

  // Load the first page on mount; stop polling on unmount.
  useEffect(() => {
    fetchRuns(0);
    return () => clearPoll();
  }, [fetchRuns, clearPoll]);

  const toggleMetric = (m: string) =>
    setMetrics((prev) => (prev.includes(m) ? prev.filter((x) => x !== m) : [...prev, m]));

  const onRun = async () => {
    if (!datasetName.trim()) {
      setError('Dataset name is required.');
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const body: any = { dataset_name: datasetName.trim() };
      if (metrics.length) body.metrics = metrics;
      if (maxItems.trim()) body.max_items = parseInt(maxItems, 10);
      if (mode) body.mode = mode;
      await evaluationApi.createRun(body);
      // Jump to the first page — the new run shows at the top and polling starts.
      await fetchRuns(0);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to start eval run');
    } finally {
      setSubmitting(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="space-y-6 p-6">
      <div>
        <div className="flex items-center gap-3 mb-2">
          <FlaskConical className="h-8 w-8 text-primary-600" />
          <h1 className="page-header">Evaluations</h1>
        </div>
        <p className="page-subtitle">
          Trigger an LLM evaluation run over a Langfuse dataset and watch its progress. Detailed
          per-item scores are recorded in Langfuse under the run name.
        </p>
      </div>

      {/* ---- Trigger form ---- */}
      <div className="card p-6 space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">Dataset name</label>
            <input
              className="input w-full"
              value={datasetName}
              onChange={(e) => setDatasetName(e.target.value)}
              placeholder="test_eval"
              disabled={submitting}
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">Mode</label>
            <select
              className="input w-full"
              value={mode}
              onChange={(e) => setMode(e.target.value)}
              disabled={submitting}
            >
              <option value="">default (per dataset)</option>
              <option value="normal">normal</option>
              <option value="deep">deep</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">
              Max items <span className="text-slate-400 font-normal">(optional)</span>
            </label>
            <input
              className="input w-full"
              type="number"
              min={1}
              value={maxItems}
              onChange={(e) => setMaxItems(e.target.value)}
              placeholder="all"
              disabled={submitting}
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">Metrics</label>
            <div className="flex flex-wrap gap-3 pt-1">
              {ALL_METRICS.map((m) => (
                <label key={m} className="flex items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={metrics.includes(m)}
                    onChange={() => toggleMetric(m)}
                    disabled={submitting}
                  />
                  {m}
                </label>
              ))}
            </div>
            <p className="text-xs text-slate-400 mt-1">
              Leave all unchecked to use the dataset's default metrics.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            className="btn btn-primary flex items-center gap-2"
            onClick={onRun}
            disabled={submitting}
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
            {submitting ? 'Starting…' : 'Run evaluation'}
          </button>
          <span className="text-xs text-slate-400">Polling every {POLL_MS / 1000}s</span>
        </div>

        {error && (
          <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
            {error}
          </div>
        )}
      </div>

      {/* ---- Runs (persisted, paginated) ---- */}
      <div className="card p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="section-header">Runs</h2>
          {loadingRuns && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
        </div>

        {runs.length === 0 && !loadingRuns ? (
          <p className="text-sm text-slate-400">No evaluation runs yet.</p>
        ) : (
          <div className="space-y-3">
            {runs.map((run) => (
              <RunCard key={run.run_id} run={run} />
            ))}
          </div>
        )}

        {total > PAGE_SIZE && (
          <div className="flex items-center justify-between pt-2">
            <span className="text-xs text-slate-500">
              Page {page + 1} of {totalPages} · {total} run{total === 1 ? '' : 's'}
            </span>
            <div className="flex items-center gap-2">
              <button
                className="btn btn-secondary flex items-center gap-1"
                onClick={() => fetchRuns(page - 1)}
                disabled={page <= 0 || loadingRuns}
              >
                <ChevronLeft className="w-4 h-4" /> Prev
              </button>
              <button
                className="btn btn-secondary flex items-center gap-1"
                onClick={() => fetchRuns(page + 1)}
                disabled={page + 1 >= totalPages || loadingRuns}
              >
                Next <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
